import { TagFilter } from '@components/core-ui/Tag/variations/TagFilter';
import { TagFilterActive } from '@components/core-ui/Tag/variations/TagFilterActive';
import type { ReactNode } from 'react';

import { Wrapper } from './styles/Wrapper';

function StatusFilterChip({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className="flex min-h-11 shrink-0 items-center rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-link"
    >
      {selected ? <TagFilterActive>{children}</TagFilterActive> : <TagFilter>{children}</TagFilter>}
    </button>
  );
}

export type StatusFilters = {
  all: boolean;
  JOB_STATUS_DRAFT: boolean;
  JOB_STATUS_PROCESSING: boolean;
  JOB_STATUS_TRANSCRIBING: boolean;
  JOB_STATUS_PROOFING: boolean;
  JOB_STATUS_COMPLETE: boolean;
  JOB_STATUS_READY_FOR_TRANSCRIBING: boolean;
  JOB_STATUS_READY_FOR_PROOFREADING: boolean;
  JOB_STATUS_READY_FOR_FINAL: boolean;
  JOB_STATUS_FINALIZING: boolean;
  AS_ONE_LOCKED: boolean;
};

type TProps = {
  statusFilters: StatusFilters;
  setStatusFilters: (value: StatusFilters) => void;
};

export const StatusFilters = ({ statusFilters, setStatusFilters }: TProps) => {
  const changeStatusFilters = (key: keyof typeof statusFilters) => {
    if (key === 'all' && !statusFilters.all) {
      setStatusFilters({
        all: true,
        JOB_STATUS_DRAFT: false,
        JOB_STATUS_PROCESSING: false,
        JOB_STATUS_TRANSCRIBING: false,
        JOB_STATUS_PROOFING: false,
        JOB_STATUS_COMPLETE: false,
        JOB_STATUS_READY_FOR_TRANSCRIBING: false,
        JOB_STATUS_READY_FOR_PROOFREADING: false,
        JOB_STATUS_READY_FOR_FINAL: false,
        JOB_STATUS_FINALIZING: false,
        AS_ONE_LOCKED: false,
      });
      return;
    }
    if (key === 'all' && statusFilters.all) {
      setStatusFilters({
        all: false,
        JOB_STATUS_DRAFT: true,
        JOB_STATUS_PROCESSING: true,
        JOB_STATUS_TRANSCRIBING: false,
        JOB_STATUS_PROOFING: false,
        JOB_STATUS_COMPLETE: false,
        JOB_STATUS_READY_FOR_TRANSCRIBING: false,
        JOB_STATUS_READY_FOR_PROOFREADING: false,
        JOB_STATUS_READY_FOR_FINAL: false,
        JOB_STATUS_FINALIZING: false,
        AS_ONE_LOCKED: false,
      });
      return;
    }
    if (!statusFilters[key]) {
      setStatusFilters({ ...statusFilters, all: false, [key]: !statusFilters[key] });
      return;
    }
    const truthyKey = Object.keys(statusFilters).find(
      (k) => k !== key && k !== 'all' && statusFilters[k as keyof typeof statusFilters],
    );
    setStatusFilters({ ...statusFilters, all: !truthyKey, [key]: !statusFilters[key] });
  };

  return (
    <Wrapper className="status-filter" role="group" aria-label="Filter jobs by status">
      <div className="name hidden md:block">Filter by status:</div>
      <StatusFilterChip selected={statusFilters.all} onClick={() => changeStatusFilters('all')}>
        All
      </StatusFilterChip>
      <StatusFilterChip
        selected={statusFilters.JOB_STATUS_DRAFT}
        onClick={() => changeStatusFilters('JOB_STATUS_DRAFT')}
      >
        Draft
      </StatusFilterChip>
      <StatusFilterChip
        selected={statusFilters.JOB_STATUS_PROCESSING}
        onClick={() => changeStatusFilters('JOB_STATUS_PROCESSING')}
      >
        Processing
      </StatusFilterChip>
      <StatusFilterChip
        selected={statusFilters.JOB_STATUS_TRANSCRIBING}
        onClick={() => changeStatusFilters('JOB_STATUS_TRANSCRIBING')}
      >
        Editing
      </StatusFilterChip>
      <StatusFilterChip
        selected={statusFilters.JOB_STATUS_PROOFING}
        onClick={() => changeStatusFilters('JOB_STATUS_PROOFING')}
      >
        Proofreading
      </StatusFilterChip>
      <StatusFilterChip
        selected={statusFilters.JOB_STATUS_COMPLETE}
        onClick={() => changeStatusFilters('JOB_STATUS_COMPLETE')}
      >
        Complete
      </StatusFilterChip>
      <StatusFilterChip
        selected={statusFilters.JOB_STATUS_READY_FOR_TRANSCRIBING}
        onClick={() => changeStatusFilters('JOB_STATUS_READY_FOR_TRANSCRIBING')}
      >
        Ready for editing
      </StatusFilterChip>
      <StatusFilterChip
        selected={statusFilters.JOB_STATUS_READY_FOR_PROOFREADING}
        onClick={() => changeStatusFilters('JOB_STATUS_READY_FOR_PROOFREADING')}
      >
        Ready for proofreading
      </StatusFilterChip>
      <StatusFilterChip
        selected={statusFilters.JOB_STATUS_READY_FOR_FINAL}
        onClick={() => changeStatusFilters('JOB_STATUS_READY_FOR_FINAL')}
      >
        Ready for final
      </StatusFilterChip>
      <StatusFilterChip
        selected={statusFilters.JOB_STATUS_FINALIZING}
        onClick={() => changeStatusFilters('JOB_STATUS_FINALIZING')}
      >
        Finalizing
      </StatusFilterChip>
      <StatusFilterChip selected={statusFilters.AS_ONE_LOCKED} onClick={() => changeStatusFilters('AS_ONE_LOCKED')}>
        Locked
      </StatusFilterChip>
    </Wrapper>
  );
};
